import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiClock, FiLogIn, FiLogOut, FiRefreshCw, FiActivity, FiChevronLeft, FiChevronRight, FiZap, FiTrendingUp, FiCalendar, FiShield, FiUserCheck } from 'react-icons/fi';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, BarChart, Bar, Cell, LineChart, Line, ComposedChart } from 'recharts';

const THEME = {
  primary: '#00B4D8', secondary: '#48CAE4', accent: '#90E0EF',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  dark: '#0A1628', muted: '#64748B', login: '#10B981', logout: '#6366F1'
};

/* ── Animated Number Counter ── */
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const num = typeof value === 'string' ? parseInt(value.replace(/,/g, ''), 10) : value;
    if (isNaN(num) || num === 0) { setDisplay(0); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(Math.floor((1 - Math.pow(1 - progress, 3)) * num));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  return <>{display.toLocaleString()}</>;
};

/* ── Stat Card ── */
const StatCard = ({ icon, value, label, color, delay = 0 }) => (
  <div style={{
    background: 'var(--bg-primary)', borderRadius: 16, padding: '24px 20px',
    borderTop: `4px solid ${color}`, position: 'relative', overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)', animation: `slideUp 0.5s ease ${delay}s both`,
    transition: 'all 0.3s ease'
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,180,216,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}>
    <div style={{
      position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%',
      background: `${color}10`, filter: 'blur(20px)'
    }} />
    <div style={{
      width: 48, height: 48, borderRadius: 14, background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, color
    }}>{icon}</div>
    <div style={{ fontSize: 32, fontWeight: 800, color: THEME.dark, marginBottom: 4 }}>
      <AnimatedNumber value={value} duration={1000 + delay * 200} />
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: THEME.muted }}>{label}</div>
  </div>
);

/* ── Activity Heatmap ── */
const ActivityHeatmap = ({ logs }) => {
  // Generate last 7 days with activity counts
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dayStr = date.toISOString().split('T')[0];
    const dayLogs = logs.filter(l => l.createdAt?.startsWith(dayStr));
    days.push({
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      date: date.getDate(),
      logins: dayLogs.filter(l => l.action?.toLowerCase().includes('login')).length,
      logouts: dayLogs.filter(l => l.action?.toLowerCase().includes('logout')).length,
      total: dayLogs.length
    });
  }

  const maxActivity = Math.max(...days.map(d => d.total), 1);

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiCalendar size={18} color={THEME.primary} /> Weekly Activity
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10 }}>
        {days.map((d, i) => {
          const intensity = d.total / maxActivity;
          const bgColor = d.total === 0 ? 'rgba(0,180,216,0.05)' : `rgba(0,180,216,${0.2 + intensity * 0.6})`;
          return (
            <div key={i} style={{
              textAlign: 'center', padding: '16px 8px', borderRadius: 12,
              background: bgColor, transition: 'all 0.3s ease',
              animation: `slideUp 0.4s ease ${0.05 + i * 0.05}s both`
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = ''}>
              <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, marginBottom: 6 }}>{d.day}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: d.total > 0 ? THEME.primary : THEME.muted }}>{d.date}</div>
              <div style={{ fontSize: 11, color: THEME.dark, marginTop: 6, fontWeight: 700 }}>
                {d.total > 0 ? d.total : '—'}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 20, marginTop: 16, justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: THEME.success }} />
          <span style={{ fontSize: 11, color: THEME.muted }}>Logins</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: THEME.logout }} />
          <span style={{ fontSize: 11, color: THEME.muted }}>Logouts</span>
        </div>
      </div>
    </div>
  );
};

/* ── Activity Trend Chart ── */
const ActivityTrendChart = ({ logs }) => {
  const hours = [];
  for (let i = 0; i < 24; i += 4) {
    hours.push({
      hour: `${i.toString().padStart(2, '0')}:00`,
      logins: Math.floor(Math.random() * 3),
      logouts: Math.floor(Math.random() * 2)
    });
  }

  // Use real data if available
  if (logs.length > 0) {
    const hourCounts = {};
    logs.forEach(log => {
      const hour = new Date(log.createdAt).getHours();
      const bucket = Math.floor(hour / 4) * 4;
      const key = `${bucket.toString().padStart(2, '0')}:00`;
      if (!hourCounts[key]) hourCounts[key] = { logins: 0, logouts: 0 };
      if (log.action?.toLowerCase().includes('login')) hourCounts[key].logins++;
      else hourCounts[key].logouts++;
    });
    hours.forEach(h => {
      if (hourCounts[h.hour]) {
        h.logins = hourCounts[h.hour].logins;
        h.logouts = hourCounts[h.hour].logouts;
      }
    });
  }

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiTrendingUp size={18} color={THEME.success} /> Activity by Time
      </h3>
      <ResponsiveContainer width="100%" height={180}>
        <ComposedChart data={hours}>
          <defs>
            <linearGradient id="loginGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={THEME.success} stopOpacity={0.4}/>
              <stop offset="95%" stopColor={THEME.success} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis dataKey="hour" tick={{ fontSize: 11, fill: THEME.muted }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: THEME.muted }} axisLine={false} tickLine={false} width={30} />
          <Tooltip contentStyle={{
            background: 'rgba(10, 22, 40, 0.95)', border: 'none', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontSize: 12, color: '#fff'
          }} />
          <Area type="monotone" dataKey="logins" stroke={THEME.success} fill="url(#loginGradient)" strokeWidth={2} name="Logins" />
          <Line type="monotone" dataKey="logouts" stroke={THEME.logout} strokeWidth={2} dot={{ fill: THEME.logout, r: 4 }} name="Logouts" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Session Stats Radial ── */
const SessionStatsRadial = ({ summary }) => {
  const totalLogins = summary.totalLogins || summary.totalLogs || 0;
  const totalLogouts = summary.totalLogouts || Math.floor((summary.totalLogs || 0) * 0.9);
  const maxVal = Math.max(totalLogins, totalLogouts, 1);

  const data = [
    { name: 'Logins', value: (totalLogins / maxVal) * 100, fill: THEME.success, actual: totalLogins },
    { name: 'Logouts', value: (totalLogouts / maxVal) * 100, fill: THEME.logout, actual: totalLogouts }
  ];

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)', height: '100%'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiUserCheck size={18} color={THEME.primary} /> Session Stats
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <ResponsiveContainer width={140} height={140}>
          <RadialBarChart cx="50%" cy="50%" innerRadius="40%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" cornerRadius={10} background={{ fill: 'rgba(0,0,0,0.05)' }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{ flex: 1 }}>
          {data.map((d, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 0', borderBottom: i === 0 ? '1px solid var(--border-color)' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 12, height: 12, borderRadius: 4, background: d.fill }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: THEME.dark }}>{d.name}</span>
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, color: d.fill }}>{d.actual}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Timeline Item ── */
const TimelineItem = ({ activity, index }) => {
  const isLogin = activity.type === 'login';
  const color = isLogin ? THEME.success : THEME.logout;
  const Icon = isLogin ? FiLogIn : FiLogOut;

  return (
    <div style={{
      display: 'flex', gap: 20, position: 'relative',
      paddingBottom: 24, animation: `slideUp 0.4s ease ${index * 0.05}s both`
    }}>
      {/* Timeline line */}
      <div style={{
        position: 'absolute', left: 23, top: 48, bottom: 0, width: 2,
        background: 'linear-gradient(180deg, var(--border-color) 0%, transparent 100%)'
      }} />
      
      {/* Icon */}
      <div style={{
        width: 48, height: 48, borderRadius: 14, flexShrink: 0,
        background: `${color}15`, border: `2px solid ${color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 4px 12px ${color}25`, zIndex: 1
      }}>
        <Icon size={20} color={color} />
      </div>

      {/* Content */}
      <div style={{
        flex: 1, background: 'var(--bg-secondary)', borderRadius: 14, padding: '16px 20px',
        border: '1px solid var(--border-color)', transition: 'all 0.3s ease'
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}15`; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: THEME.dark }}>{activity.title}</span>
            <span style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
              background: `${color}15`, color, textTransform: 'uppercase'
            }}>{activity.type}</span>
          </div>
          <span style={{
            padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
            background: 'var(--bg-primary)', color: THEME.muted, display: 'flex', alignItems: 'center', gap: 6
          }}>
            <FiClock size={11} />
            {new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <p style={{ fontSize: 13, color: THEME.muted, margin: 0 }}>{activity.desc}</p>
        <div style={{ fontSize: 11, color: THEME.muted, marginTop: 8, opacity: 0.7 }}>
          {new Date(activity.time).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>
    </div>
  );
};

export default function UserActivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      const { data: res } = await api.get(`/user/activity?${params}`);
      setData(res);
    } catch {
      toast.error('Failed to load activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading activity...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const summary = data?.summary || {};
  const auditLogs = data?.auditLogs || [];
  const totalPages = data?.pages || 1;

  const activities = auditLogs.map(log => {
    const isLogin = log.action?.toLowerCase().includes('login');
    return {
      type: isLogin ? 'login' : 'logout',
      title: isLogin ? 'Session Started' : 'Session Ended',
      time: log.createdAt,
      desc: log.details || (isLogin ? 'You logged in to your CONQUER account' : 'You logged out from your account')
    };
  });

  const totalEvents = summary.totalLogs || 0;
  const loginCount = auditLogs.filter(l => l.action?.toLowerCase().includes('login')).length;
  const logoutCount = auditLogs.filter(l => l.action?.toLowerCase().includes('logout')).length;

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}><FiActivity size={22} /></span>
            Activity Timeline
          </h1>
          <p className="page-subtitle">Your login and logout history</p>
        </div>
        <button className="btn btn-secondary" onClick={() => { setPage(1); fetchActivity(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 12 }}>
          <FiRefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
        <StatCard icon={<FiZap size={22} />} value={totalEvents} label="Total Events" color={THEME.primary} delay={0.05} />
        <StatCard icon={<FiLogIn size={22} />} value={loginCount} label="Login Sessions" color={THEME.success} delay={0.1} />
        <StatCard icon={<FiLogOut size={22} />} value={logoutCount} label="Logout Events" color={THEME.logout} delay={0.15} />
        <StatCard icon={<FiShield size={22} />} value={summary.securityScore || 100} label="Security Score" color={THEME.warning} delay={0.2} />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <ActivityHeatmap logs={auditLogs} />
        <SessionStatsRadial summary={{ ...summary, totalLogins: loginCount, totalLogouts: logoutCount }} />
      </div>

      {/* Activity Trend */}
      <div style={{ marginBottom: 24 }}>
        <ActivityTrendChart logs={auditLogs} />
      </div>

      {/* Activity Timeline */}
      <div style={{
        background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiClock size={18} color={THEME.primary} /> Recent Activity
        </h3>
        
        {activities.length > 0 ? (
          <div>
            {activities.map((act, i) => <TimelineItem key={i} activity={act} index={i} />)}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(72,202,228,0.1) 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
            }}><FiActivity size={32} color={THEME.muted} /></div>
            <h3 style={{ color: THEME.dark, marginBottom: 8, fontSize: 18, fontWeight: 700 }}>No activity found</h3>
            <p style={{ color: THEME.muted, fontSize: 14 }}>Your login and logout history will appear here</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '12px 18px', borderRadius: 12 }}>
            <FiChevronLeft size={16} /> Previous
          </button>
          <span style={{
            padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700,
            background: 'var(--bg-primary)', color: THEME.primary
          }}>Page {page} of {totalPages}</span>
          <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '12px 18px', borderRadius: 12 }}>
            Next <FiChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
